import { Suspense, lazy, useEffect, useMemo, useState } from 'react'
import { Box, Card, Group, Stack, Tabs, Text } from '@mantine/core'
import { Navigate, useSearchParams } from 'react-router-dom'
import { IconSettings } from '@/components/icons'
import { PageCenterLoader, PageShell, PageToolbar } from '@/components/ui'
import { useAuth } from '@/lib/auth-context'
import { api } from '@/lib/api'
import {
  canAccessProjectSettings,
  canManageOperatorRouting,
  defaultPermissionsForRole,
  hasPermission,
  loadMyPermissions,
  readCachedPermissions,
} from '@/lib/permissions'
import type { MyPermissionsResponse, RolePermissionItem } from '@/types/api'
import { isRoleListedInStaffPickers } from '@/lib/operator-role-labels'
const SettingsMacrosTab = lazy(async () =>
  import('./SettingsMacrosTab').then((m) => ({ default: m.SettingsMacrosTab })),
)
const SettingsWeightsTab = lazy(async () =>
  import('./SettingsWeightsTab').then((m) => ({ default: m.SettingsWeightsTab })),
)
const SettingsOperatorRoutingTab = lazy(async () =>
  import('./SettingsOperatorRoutingTab').then((m) => ({ default: m.SettingsOperatorRoutingTab })),
)
const SettingsProjectsTab = lazy(async () =>
  import('./SettingsProjectsTab').then((m) => ({ default: m.SettingsProjectsTab })),
)
const SettingsRolesTab = lazy(async () =>
  import('./SettingsRolesTab').then((m) => ({ default: m.SettingsRolesTab })),
)
const SettingsTriggersTab = lazy(async () =>
  import('./SettingsTriggersTab').then((m) => ({ default: m.SettingsTriggersTab })),
)
const SettingsWorkspaceTab = lazy(async () =>
  import('./SettingsWorkspaceTab').then((m) => ({ default: m.SettingsWorkspaceTab })),
)

const ADMIN_TABS = ['workspace', 'roles', 'projects', 'weights', 'routing', 'triggers', 'macros'] as const
type AdminTab = (typeof ADMIN_TABS)[number]

/** Вкладки настроек по правам из матрицы workspace, а не по системному имени роли (operator/admin/…). */
function visibleSettingsTabs(perm: MyPermissionsResponse): AdminTab[] {
  const tabs: AdminTab[] = []
  if (hasPermission(perm, 'access_settings') || hasPermission(perm, 'manage_workspace_settings')) {
    tabs.push('workspace')
  }
  if (perm.can_manage_permissions) tabs.push('roles')
  if (canAccessProjectSettings(perm)) {
    tabs.push('projects')
    tabs.push('weights')
  }
  if (canManageOperatorRouting(perm)) {
    tabs.push('routing')
  }
  if (hasPermission(perm, 'manage_triggers')) tabs.push('triggers')
  tabs.push('macros')
  return tabs
}

function resolveActiveTab(raw: string | null, visible: AdminTab[]): AdminTab {
  if (raw && (visible as readonly string[]).includes(raw)) return raw as AdminTab
  return visible[0] ?? 'workspace'
}

export function SettingsPage() {
  const { user } = useAuth()
  const [searchParams, setSearchParams] = useSearchParams()
  const [perm, setPerm] = useState<MyPermissionsResponse | null>(() => readCachedPermissions())
  const [roleMatrix, setRoleMatrix] = useState<RolePermissionItem[]>([])

  useEffect(() => {
    void loadMyPermissions(user?.role)
      .then(setPerm)
      .catch(() => setPerm(defaultPermissionsForRole(user?.role)))
  }, [user?.role])

  const effectivePerm = perm ?? defaultPermissionsForRole(user?.role)
  const userRole = (user?.role || '').toLowerCase()

  const canManageWorkspace = hasPermission(effectivePerm, 'manage_workspace_settings')
  const canManageRoles = !!effectivePerm.can_manage_permissions
  const canManageProjects = canAccessProjectSettings(effectivePerm)
  const canManageRouting = canManageOperatorRouting(effectivePerm)
  const canManageTriggers = hasPermission(effectivePerm, 'manage_triggers')
  const isOwnerOrAdmin = userRole === 'owner' || userRole === 'admin'

  const visibleTabs = useMemo(() => visibleSettingsTabs(effectivePerm), [effectivePerm])
  const canEnterSettings = visibleTabs.length > 0

  const tabFromUrl = searchParams.get('tab')
  const activeTab = resolveActiveTab(tabFromUrl, visibleTabs)
  const [visitedTabs, setVisitedTabs] = useState<Set<AdminTab>>(new Set())

  useEffect(() => {
    setVisitedTabs((prev) => {
      if (prev.has(activeTab)) return prev
      const next = new Set(prev)
      next.add(activeTab)
      return next
    })
  }, [activeTab])

  useEffect(() => {
    const needsRoleMatrix =
      (visibleTabs.includes('workspace') && visitedTabs.has('workspace')) ||
      (visibleTabs.includes('routing') && visitedTabs.has('routing'))
    if (!needsRoleMatrix) return
    let cancelled = false
    void api
      .workspaceRolePermissions()
      .then((res) => {
        if (cancelled) return
        setRoleMatrix(
          (Array.isArray(res?.items) ? res.items : []).filter((r) =>
            isRoleListedInStaffPickers(r?.role, { includeOwner: true }),
          ),
        )
      })
      .catch(() => {
        if (!cancelled) setRoleMatrix([])
      })
    return () => {
      cancelled = true
    }
  }, [visibleTabs, visitedTabs])

  useEffect(() => {
    if (visibleTabs.length === 0) return
    const ok = tabFromUrl && (visibleTabs as readonly string[]).includes(tabFromUrl)
    if (!ok) {
      setSearchParams(
        (prev) => {
          const n = new URLSearchParams(prev)
          n.set('tab', visibleTabs[0])
          return n
        },
        { replace: true },
      )
    }
  }, [tabFromUrl, visibleTabs, setSearchParams])

  const setTab = (value: string | null) => {
    const next = new URLSearchParams(searchParams)
    if (value) next.set('tab', value)
    else next.delete('tab')
    setSearchParams(next, { replace: true })
  }

  if (!canEnterSettings) {
    return <Navigate to="/tickets" replace />
  }

  if (tabFromUrl === 'loadtest' && isOwnerOrAdmin) {
    return <Navigate to="/debug?tab=loadtest" replace />
  }

  return (
    <PageShell>
      <Stack gap="md">
        <PageToolbar
          leading={
            <Box>
              <Group gap="xs" wrap="nowrap">
                <IconSettings size={20} stroke={1.6} />
                <Text fw={700} size="lg">
                  Настройки
                </Text>
              </Group>
              <Text size="sm" c="dimmed">
                Рабочее пространство, роли, проекты, веса, распределение, триггеры и макросы.
              </Text>
            </Box>
          }
        />
        <Card withBorder padding="md" radius="md">
          <Tabs value={activeTab} onChange={(v) => setTab(v)}>
            <Tabs.List>
              {visibleTabs.includes('workspace') ? (
                <Tabs.Tab value="workspace">Рабочее пространство</Tabs.Tab>
              ) : null}
              {visibleTabs.includes('roles') ? <Tabs.Tab value="roles">Роли</Tabs.Tab> : null}
              {visibleTabs.includes('projects') ? <Tabs.Tab value="projects">Проекты</Tabs.Tab> : null}
              {visibleTabs.includes('weights') ? <Tabs.Tab value="weights">Веса</Tabs.Tab> : null}
              {visibleTabs.includes('routing') ? <Tabs.Tab value="routing">Распределение</Tabs.Tab> : null}
              {visibleTabs.includes('triggers') ? <Tabs.Tab value="triggers">Триггеры</Tabs.Tab> : null}
              {visibleTabs.includes('macros') ? <Tabs.Tab value="macros">Макросы</Tabs.Tab> : null}
            </Tabs.List>

            {visibleTabs.includes('workspace') ? (
              <Tabs.Panel value="workspace" pt="md">
                {visitedTabs.has('workspace') ? (
                  <Suspense fallback={<PageCenterLoader label="Загрузка настроек" minHeight={180} />}>
                    <SettingsWorkspaceTab roleMatrix={roleMatrix} canEdit={canManageWorkspace} />
                  </Suspense>
                ) : null}
              </Tabs.Panel>
            ) : null}

            {visibleTabs.includes('roles') ? (
              <Tabs.Panel value="roles" pt="md">
                {visitedTabs.has('roles') ? (
                  <Suspense fallback={<PageCenterLoader label="Загрузка ролей" minHeight={180} />}>
                    <SettingsRolesTab canManage={canManageRoles} />
                  </Suspense>
                ) : null}
              </Tabs.Panel>
            ) : null}

            {visibleTabs.includes('projects') ? (
              <Tabs.Panel value="projects" pt="md">
                {visitedTabs.has('projects') ? (
                  <Suspense fallback={<PageCenterLoader label="Загрузка проектов" minHeight={180} />}>
                    <SettingsProjectsTab
                      canManageProjects={canManageProjects}
                      canManageTriggers={canManageTriggers}
                    />
                  </Suspense>
                ) : null}
              </Tabs.Panel>
            ) : null}

            {visibleTabs.includes('weights') ? (
              <Tabs.Panel value="weights" pt="md">
                {visitedTabs.has('weights') ? (
                  <Suspense fallback={<PageCenterLoader label="Загрузка весов" minHeight={180} />}>
                    <SettingsWeightsTab canEdit={canManageProjects} />
                  </Suspense>
                ) : null}
              </Tabs.Panel>
            ) : null}

            {visibleTabs.includes('routing') ? (
              <Tabs.Panel value="routing" pt="md">
                {visitedTabs.has('routing') ? (
                  <Suspense fallback={<PageCenterLoader label="Загрузка распределения" minHeight={180} />}>
                    <SettingsOperatorRoutingTab roleMatrix={roleMatrix} canEdit={canManageRouting} />
                  </Suspense>
                ) : null}
              </Tabs.Panel>
            ) : null}

            {visibleTabs.includes('triggers') ? (
              <Tabs.Panel value="triggers" pt="md">
                {visitedTabs.has('triggers') ? (
                  <Suspense fallback={<PageCenterLoader label="Загрузка триггеров" minHeight={180} />}>
                    <SettingsTriggersTab canManage={canManageTriggers} />
                  </Suspense>
                ) : null}
              </Tabs.Panel>
            ) : null}

            {visibleTabs.includes('macros') ? (
              <Tabs.Panel value="macros" pt="md">
                {visitedTabs.has('macros') ? (
                  <Suspense fallback={<PageCenterLoader label="Загрузка макросов" minHeight={180} />}>
                    <SettingsMacrosTab />
                  </Suspense>
                ) : null}
              </Tabs.Panel>
            ) : null}
          </Tabs>
        </Card>
      </Stack>
    </PageShell>
  )
}
